import { action, computed, makeObservable, observable, when, IReactionDisposer } from "mobx";
import { useEffect } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import { SettingsTabBarSectionContent } from "@/pages/settings/components/tab-bar";
import { MDSBadgeVariant, MDSIconKind } from "@/mds";
import { enumModule } from "@/modules/enum";
import {
  hasAgentSearchParams,
  SETTINGS_SEARCH_PARAM,
  withoutAgentSearchParams,
} from "@/store/overlay-search-params";
import { SETTINGS_TEMPLATE_ID_SEARCH_PARAM } from "@/app/router/appRoutes";
import type { MemAgentSettingsSource, ProactivePaywallEntrySource } from "@/domains/metrics";
import type { ReminderShillUpsellAttributionContext } from "@/modules/reminder-shill-upsell-attribution";
import { clientEnvLocalBackendApiIsEnabled } from "@/modules/client-env/viteConfig";
import type { FloatingAgentMemAgentSettingsActionChannel } from "@/modules/electron/types";
import { MCP_CONNECTION_PROACTIVE_REQUIRED_MESSAGE } from "@/pages/settings/mcp-connection-copy";
import type { ComposioToolkit } from "../../pages/settings/composio-connection-apps";

const LEGACY_REMINDERS_SETTINGS_TAB_ID = "reminders";
const MCP_CONNECTIONS_FEATURE_FLAG = "mem-agent-mcp-connections";
export const USAGE_SETTINGS_FEATURE_FLAG = "settings-usage-page";

interface SettingsModalOpenOptions {
  source?: ProactivePaywallEntrySource;
  reminderShillUpsellAttributionContext?: ReminderShillUpsellAttributionContext | null;
}

interface McpConnectionPendingDisconnect {
  id: string;
  serverUrl: string;
  serverName?: string | null;
}

export interface McpComposioConnectionPendingConfirmation {
  toolkit: ComposioToolkit;
  appTitle: string;
}

export class SettingsModalStore extends AppSubStore {
  /**
   * The validated tab id from the `?settings=` query param, or null when the param
   * is absent. Fed from `useSearchParams()` via `useSyncSettingsSearchParam()`.
   * This is the source of truth — `isOpen` and `activeTab` are derived from it.
   */
  settingsParam: SettingsTabId | null = null;

  pendingOpenAfterPermissions: SettingsTabId | null = null;
  memAgentSettingsEntrySource: ProactivePaywallEntrySource | null = null;
  reminderShillUpsellAttributionContext: ReminderShillUpsellAttributionContext | null = null;
  isReminderShillSetupDialogOpen = false;
  pendingMemAgentCommunicationChannelAction: FloatingAgentMemAgentSettingsActionChannel | null =
    null;
  mcpConnectionPendingDisconnect: McpConnectionPendingDisconnect | null = null;
  mcpComposioConnectionPendingConfirmation: McpComposioConnectionPendingConfirmation | null = null;

  private pendingPermissionsDisposer: IReactionDisposer | null = null;
  private setSearchParams: SetURLSearchParams | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      SettingsModalStore,
      | "pendingPermissionsDisposer"
      | "isDebugTab"
      | "isTabAvailable"
      | "setSearchParams"
      | "updateSettingsParam"
    >(this, {
      settingsParam: observable,
      isOpen: computed,
      activeTab: computed,
      memAgentSettingsEntrySource: observable,
      reminderShillUpsellAttributionContext: observable,
      isReminderShillSetupDialogOpen: observable,
      pendingMemAgentCommunicationChannelAction: observable,
      mcpConnectionPendingDisconnect: observable,
      mcpComposioConnectionPendingConfirmation: observable,
      memAgentSettingsSource: computed,
      pendingOpenAfterPermissions: observable,
      pendingPermissionsDisposer: false,
      setSearchParams: observable,
      tabBarSections: computed,
      open: action,
      close: action,
      closeReminderShillSetupDialog: action,
      openMcpConnectionDisconnectDialog: action,
      closeMcpConnectionDisconnectDialog: action,
      openMcpComposioConnectionConfirmation: action,
      closeMcpComposioConnectionConfirmation: action,
      navigateToTab: action,
      openMemAgentCommunicationChannels: action,
      consumePendingMemAgentCommunicationChannelAction: action,
      queueOpenAfterPermissions: action,
      consumePendingOpen: action,
      setSettingsParam: action,
      isDebugTab: false,
      isTabAvailable: false,
      updateSettingsParam: false,
      useSyncSettingsSearchParam: action,
    });
  }

  get isOpen(): boolean {
    return this.settingsParam !== null;
  }

  get activeTab(): SettingsTabId {
    return this.settingsParam ?? SettingsTabId.NotificationPreferencesPage;
  }

  get memAgentSettingsSource(): MemAgentSettingsSource {
    return this.memAgentSettingsEntrySource ?? "settings";
  }

  get tabBarSections(): SettingsTabBarSectionContent[] {
    const makeItem = (
      id: SettingsTabId,
      label: string,
      icon?: MDSIconKind,
      options: {
        badgeLabel?: string;
        badgeVariant?: MDSBadgeVariant;
        tooltipLabel?: string;
      } = {}
    ) => ({
      id,
      label,
      icon,
      badgeLabel: options.badgeLabel,
      badgeVariant: options.badgeVariant,
      tooltipLabel: options.tooltipLabel,
      onClick: () => this.navigateToTab(id),
    });

    const mcpConnectionsEnabled =
      clientEnvLocalBackendApiIsEnabled() ||
      this.store.spaceAccountFeatureFlags.getFlagStatus(MCP_CONNECTIONS_FEATURE_FLAG) === true;
    const usagePageEnabled =
      this.store.spaceAccountFeatureFlags.getFlagStatus(USAGE_SETTINGS_FEATURE_FLAG) === true;

    const sections: SettingsTabBarSectionContent[] = [
      {
        id: "general-section",
        label: "General",
        items: [
          makeItem(SettingsTabId.NotificationPreferencesPage, "Notifications", MDSIconKind.Bell),
          makeItem(SettingsTabId.VoiceModePage, "Voice mode", MDSIconKind.Microphone),
          makeItem(SettingsTabId.TemplatesPage, "Templates", MDSIconKind.AlignLeft),
          ...(this.store.interface.isDesktop
            ? [makeItem(SettingsTabId.EditorPage, "Editor", MDSIconKind.Pen)]
            : []),
          makeItem(SettingsTabId.AppearancePage, "Appearance", MDSIconKind.Palette),
        ],
      },
      {
        id: "mem-agent-section",
        label: "Mem Agent",
        items: [
          makeItem(SettingsTabId.MemAgentPage, "General", MDSIconKind.Settings),
          makeItem(
            SettingsTabId.MemAgentCommunicationChannelsPage,
            "Communication channels",
            MDSIconKind.Message
          ),
          ...(mcpConnectionsEnabled
            ? [
                makeItem(SettingsTabId.ConnectedToolsPage, "Connections", MDSIconKind.PuzzlePiece, {
                  badgeLabel: "Preview",
                  badgeVariant: MDSBadgeVariant.Emphasis,
                  tooltipLabel: this.store.subscriptions.hasProactiveAccess
                    ? undefined
                    : MCP_CONNECTION_PROACTIVE_REQUIRED_MESSAGE,
                }),
              ]
            : []),
        ],
      },
      {
        id: "apps-and-integrations-section",
        label: "Apps and integrations",
        items: [
          makeItem(
            SettingsTabId.CalendarAndMeetingsPage,
            "Calendar and meetings",
            MDSIconKind.Calendar
          ),
          makeItem(SettingsTabId.ChromeExtensionPage, "Chrome extension", MDSIconKind.Chrome),
          makeItem(SettingsTabId.EmailToMemPage, "Email to Mem", MDSIconKind.Envelope),
          makeItem(SettingsTabId.McpPage, "MCP", MDSIconKind.Mcp),
          makeItem(SettingsTabId.ZapierPage, "Zapier", MDSIconKind.BoltOutline),
        ],
      },
      {
        id: "developer-section",
        label: "Developer",
        items: [makeItem(SettingsTabId.ApiKeysPage, "API Keys", MDSIconKind.Code)],
      },
      {
        id: "account-section",
        label: "Account",
        items: [
          makeItem(SettingsTabId.ProfilePage, "Profile", MDSIconKind.User),
          ...(usagePageEnabled
            ? [makeItem(SettingsTabId.UsagePage, "Usage", MDSIconKind.Timeline)]
            : []),
          makeItem(SettingsTabId.ImportsPage, "My imports", MDSIconKind.Import),
          makeItem(
            SettingsTabId.SubscriptionManagementPage,
            "Subscription",
            MDSIconKind.CreditCard
          ),
          makeItem(SettingsTabId.ExportsPage, "Exports", MDSIconKind.ArrowUpFromLine),
          makeItem(SettingsTabId.ReferralProgramPage, "Referral program", MDSIconKind.Share),
        ],
      },
    ];

    if (this.store.debug.debugModeEnabled) {
      sections.push({
        id: "debug-section",
        label: "Debug",
        items: [
          makeItem(SettingsTabId.DebugAccountPage, "Debug account", MDSIconKind.GearCode),
          makeItem(SettingsTabId.DebugSandboxPage, "Debug sandbox", MDSIconKind.GearCode),
        ],
      });
    }

    return sections;
  }

  private isDebugTab(tabId: SettingsTabId): boolean {
    return tabId === SettingsTabId.DebugAccountPage || tabId === SettingsTabId.DebugSandboxPage;
  }

  private isTabAvailable(tabId: SettingsTabId): boolean {
    if (tabId === SettingsTabId.ChatPage) {
      return false;
    }

    if (tabId === SettingsTabId.EditorPage && !this.store.interface.isDesktop) {
      return false;
    }

    if (this.isDebugTab(tabId) && !this.store.debug.debugModeEnabled) {
      return false;
    }

    if (
      tabId === SettingsTabId.ConnectedToolsPage &&
      !clientEnvLocalBackendApiIsEnabled() &&
      this.store.spaceAccountFeatureFlags.getFlagStatus(MCP_CONNECTIONS_FEATURE_FLAG) !== true
    ) {
      return false;
    }

    if (
      tabId === SettingsTabId.UsagePage &&
      this.store.spaceAccountFeatureFlags.getFlagStatus(USAGE_SETTINGS_FEATURE_FLAG) !== true
    ) {
      return false;
    }

    return true;
  }

  /**
   * Writes the `?settings=` query param to the URL. Pass null to remove it.
   * Uses `replace` to avoid polluting browser history with tab switches.
   */
  private updateSettingsParam(
    tabId: SettingsTabId | null,
    options: { templateId?: string | null } = {}
  ) {
    if (!this.setSearchParams) return;

    this.setSearchParams(
      prevParams => {
        let nextParams = new URLSearchParams(prevParams);

        if (tabId) {
          nextParams = withoutAgentSearchParams({ searchParams: nextParams });
          nextParams.set(SETTINGS_SEARCH_PARAM, tabId);
        } else {
          nextParams.delete(SETTINGS_SEARCH_PARAM);
        }

        if (tabId === SettingsTabId.TemplatesPage && options.templateId) {
          nextParams.set(SETTINGS_TEMPLATE_ID_SEARCH_PARAM, options.templateId);
        } else {
          nextParams.delete(SETTINGS_TEMPLATE_ID_SEARCH_PARAM);
        }

        return nextParams;
      },
      { replace: true }
    );
  }

  open(tabId?: SettingsTabId, options: SettingsModalOpenOptions = {}) {
    const resolvedTab = tabId ?? SettingsTabId.NotificationPreferencesPage;
    if (!this.isTabAvailable(resolvedTab)) return;

    this.memAgentSettingsEntrySource =
      resolvedTab === SettingsTabId.MemAgentPage ? (options.source ?? null) : null;
    this.reminderShillUpsellAttributionContext =
      resolvedTab === SettingsTabId.MemAgentPage
        ? (options.reminderShillUpsellAttributionContext ?? null)
        : null;
    this.isReminderShillSetupDialogOpen =
      resolvedTab === SettingsTabId.MemAgentPage &&
      options.source === "editor_reminder_shill" &&
      options.reminderShillUpsellAttributionContext !== undefined;
    this.updateSettingsParam(resolvedTab);
  }

  close() {
    this.memAgentSettingsEntrySource = null;
    this.reminderShillUpsellAttributionContext = null;
    this.isReminderShillSetupDialogOpen = false;
    this.pendingMemAgentCommunicationChannelAction = null;
    this.mcpConnectionPendingDisconnect = null;
    this.mcpComposioConnectionPendingConfirmation = null;
    this.updateSettingsParam(null);
  }

  closeReminderShillSetupDialog() {
    this.isReminderShillSetupDialogOpen = false;
  }

  openMcpConnectionDisconnectDialog(connection: McpConnectionPendingDisconnect) {
    this.mcpConnectionPendingDisconnect = connection;
  }

  closeMcpConnectionDisconnectDialog() {
    this.mcpConnectionPendingDisconnect = null;
  }

  openMcpComposioConnectionConfirmation(pending: McpComposioConnectionPendingConfirmation) {
    this.mcpComposioConnectionPendingConfirmation = pending;
  }

  closeMcpComposioConnectionConfirmation() {
    this.mcpComposioConnectionPendingConfirmation = null;
  }

  navigateToTab(tabId: SettingsTabId) {
    if (!this.isTabAvailable(tabId)) return;

    if (tabId !== SettingsTabId.MemAgentCommunicationChannelsPage) {
      this.pendingMemAgentCommunicationChannelAction = null;
    }

    if (tabId !== SettingsTabId.MemAgentPage || this.activeTab !== SettingsTabId.MemAgentPage) {
      this.memAgentSettingsEntrySource = null;
      this.reminderShillUpsellAttributionContext = null;
      this.isReminderShillSetupDialogOpen = false;
    }
    this.updateSettingsParam(tabId);
  }

  openMemAgentCommunicationChannels(channel?: FloatingAgentMemAgentSettingsActionChannel) {
    this.pendingMemAgentCommunicationChannelAction = channel ?? null;
    this.open(SettingsTabId.MemAgentCommunicationChannelsPage);
  }

  consumePendingMemAgentCommunicationChannelAction() {
    const channel = this.pendingMemAgentCommunicationChannelAction;
    this.pendingMemAgentCommunicationChannelAction = null;
    return channel;
  }

  queueOpenAfterPermissions(tabId: SettingsTabId) {
    this.pendingOpenAfterPermissions = tabId;

    // Dispose any existing watcher
    this.pendingPermissionsDisposer?.();

    // Set up a one-shot watcher: when the system permissions modal closes,
    // open the settings modal on the queued tab.
    this.pendingPermissionsDisposer = when(
      () =>
        this.pendingOpenAfterPermissions !== null &&
        !this.store.systemPermissions.ui.isAnyModalOpen,
      () => {
        this.consumePendingOpen();
        this.pendingPermissionsDisposer = null;
      }
    );
  }

  consumePendingOpen() {
    if (this.pendingOpenAfterPermissions === null) return;

    const tabId = this.pendingOpenAfterPermissions;
    this.pendingOpenAfterPermissions = null;
    this.pendingPermissionsDisposer?.();
    this.pendingPermissionsDisposer = null;
    this.open(tabId);
  }

  /**
   * Sets the validated settings param from the URL. Called by the hook below.
   */
  setSettingsParam(tabId: SettingsTabId | null) {
    if (tabId !== SettingsTabId.MemAgentCommunicationChannelsPage) {
      this.pendingMemAgentCommunicationChannelAction = null;
    }

    if (tabId !== SettingsTabId.MemAgentPage) {
      this.memAgentSettingsEntrySource = null;
      this.reminderShillUpsellAttributionContext = null;
      this.isReminderShillSetupDialogOpen = false;
    }
    this.settingsParam = tabId;
  }

  /**
   * Syncs the `?settings=<tab-id>` query param into this store, following the
   * same pattern as `useSyncNavigation()` which captures `navigate`.
   *
   * The URL is the source of truth. `isOpen` and `activeTab` are computed from
   * `settingsParam`, which is fed from `useSearchParams()`. Actions like `open()`
   * and `close()` write to the URL via the captured `setSearchParams`, which
   * triggers a re-render, which feeds the new value back into the store.
   *
   * Called from `AppStore.useInitializeAppStoreEffects()`.
   */
  useSyncSettingsSearchParam() {
    const [searchParams, setSearchParams] = useSearchParams();
    this.setSearchParams = setSearchParams;

    const rawSettingsParam = searchParams.get(SETTINGS_SEARCH_PARAM);
    const rawTemplateId = searchParams.get(SETTINGS_TEMPLATE_ID_SEARCH_PARAM);
    const hasAgentParams = hasAgentSearchParams({ searchParams });

    useEffect(() => {
      if (rawSettingsParam) {
        const validTab =
          rawSettingsParam === LEGACY_REMINDERS_SETTINGS_TAB_ID
            ? SettingsTabId.MemAgentPage
            : enumModule.values(SettingsTabId).find(v => v === rawSettingsParam);
        const shouldRewriteSettingsParam = validTab !== undefined && rawSettingsParam !== validTab;

        if (validTab && this.isTabAvailable(validTab)) {
          this.setSettingsParam(validTab);
          if (validTab === SettingsTabId.TemplatesPage) {
            if (rawTemplateId) {
              this.store.settingsModalTemplatesPage.navigateToTemplateEdit(rawTemplateId);
            } else {
              this.store.settingsModalTemplatesPage.navigateToTemplateList();
            }
          }

          if (hasAgentParams || shouldRewriteSettingsParam) {
            this.updateSettingsParam(validTab, {
              templateId: validTab === SettingsTabId.TemplatesPage ? rawTemplateId : null,
            });
          }
        } else {
          this.setSettingsParam(null);
          this.updateSettingsParam(null);
        }
      } else {
        this.setSettingsParam(null);
      }
    }, [rawSettingsParam, rawTemplateId, hasAgentParams]);
  }
}
